import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text,
} from '@/components/ui';

import React from 'react';

import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from '@tabler/icons-react';
import { Table } from '@tanstack/react-table';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
  totalCount?: number; // For server-side pagination
  manualPagination?: boolean;
}

export function DataTablePagination<TData>({
  table,
  showPageSizeSelector = true,
  pageSizeOptions = [10, 25, 50, 100],
  totalCount,
  manualPagination = false,
}: DataTablePaginationProps<TData>) {
  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const selectedRowsCount = table.getFilteredSelectedRowModel().rows.length;
  // Use server-side total count for manual pagination, otherwise use client-side count
  const totalRowsCount =
    manualPagination && totalCount !== undefined
      ? totalCount
      : table.getFilteredRowModel().rows.length;

  return (
    <div className="data-table-pagination">
      <div className="data-table-pagination__info">
        <Text size="2xsmall" color="secondary">
          {selectedRowsCount > 0 && (
            <>
              {selectedRowsCount} of {totalRowsCount} row(s) selected.
            </>
          )}
          {selectedRowsCount === 0 && (
            <>
              {totalRowsCount === 0 ? (
                'No results found'
              ) : (
                <>
                  Showing {Math.min(pageIndex * pageSize + 1, totalRowsCount)}{' '}
                  to {Math.min((pageIndex + 1) * pageSize, totalRowsCount)} of{' '}
                  {totalRowsCount} results
                </>
              )}
            </>
          )}
        </Text>
      </div>

      <div className="data-table-pagination__controls">
        {showPageSizeSelector && (
          <div className="data-table-pagination__page-size">
            <Text size="2xsmall" style={{ marginRight: '0.5rem' }}>
              Rows per page
            </Text>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<IconChevronDown size={14} />}
                  className="data-table-pagination__select"
                >
                  {pageSize}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                variant="soft"
                align="center"
                className="data-table-pagination__select-content"
              >
                {pageSizeOptions.map(size => (
                  <DropdownMenuItem
                    key={size}
                    onSelect={() => {
                      // Update page size via TanStack Table API.
                      table.setPageSize(size);
                    }}
                  >
                    <Text size="2xsmall" as="span">
                      {size}
                    </Text>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <div className="data-table-pagination__page-info">
          <Text size="2xsmall">
            Page {pageIndex + 1} of {Math.max(pageCount, 1)}
          </Text>
        </div>

        <div className="data-table-pagination__buttons">
          <Button
            variant="outlined"
            size="small"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            isIconOnly
            className="data-table-pagination__button"
          >
            <IconChevronsLeft />
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            isIconOnly
            className="data-table-pagination__button"
          >
            <IconChevronLeft />
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            isIconOnly
            className="data-table-pagination__button"
          >
            <IconChevronRight />
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => table.setPageIndex(Math.max(pageCount - 1, 0))}
            disabled={!table.getCanNextPage()}
            isIconOnly
            className="data-table-pagination__button"
          >
            <IconChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
